import React, { useState } from "react";
import { Search, Download } from "lucide-react";
import { WalletTx, PagedResponse } from "./types";
import RoundModal from "./RoundModal";
import {
  isoDay, daysAgo, fmtAmt, fmtInt, fmtDate, short, usePaged, useDebounced,
  Header, DateRange, TestToggle, ErrorBox, Panel, Pager, FilterBar, Badge, EmptyRow, PlayerCell,
  KIND_COLORS, STATUS_COLORS, inputCls, btnCls, thCls, thR, tdCls, tdR, theadRow, tbodyRow, pageCls,
} from "./shared";

const pickTx = (d: PagedResponse) => d.transactions;

const amountColor = (kind: string) =>
  kind.indexOf("WIN") >= 0 ? "text-[#0ECC68]" : kind === "ROLLBACK" ? "text-[#FFC23F]" : "";

function exportCsv(rows: WalletTx[], page: number) {
  const head = ["time", "user_id", "user", "game", "type", "status", "amount", "currency", "balance_after", "round_id", "provider_tx_id", "our_tx_id", "error"];
  const cell = (v: unknown) => `"${v === null || v === undefined ? "" : String(v).replace(/"/g, '""')}"`;
  const lines = rows.map((r) => [r.created_at, r.user_id, r.user_name, r.game_code, r.kind, r.status, r.amount_major,
    r.currency, r.balance_after, r.round_id, r.provider_tx_id, r.operator_tx_id, r.error_code].map(cell).join(","));
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" }));
  a.download = `original-games-bets-page${page}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function Bets() {
  const [search, setSearch] = useState("");
  const [userId, setUserId] = useState("");
  const [game, setGame] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [currency, setCurrency] = useState("");
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(isoDay(new Date()));
  const [includeTest, setIncludeTest] = useState(false);
  const [round, setRound] = useState<string | null>(null);
  const dSearch = useDebounced(search);
  const dUser = useDebounced(userId);
  const dGame = useDebounced(game);
  const dCur = useDebounced(currency);

  const { rows, pagination, setPage, loading, error, load } = usePaged<WalletTx>("/transactions", pickTx, {
    search: dSearch.trim(), user_id: dUser.trim(), game_code: dGame.trim().toLowerCase(),
    kind, status, currency: dCur.trim(), from, to, include_test: includeTest,
  });

  return (
    <div className={pageCls}>
      <Header title="Original Games — Bet History" subtitle="Every wallet movement: bets, wins, rollbacks and refused bets" onRefresh={load} loading={loading} />
      <ErrorBox error={error} />
      <Panel
        title={<>Transactions <span className="text-sm text-[#878AA2] font-normal ml-2">({fmtInt(pagination.totalCount)} total)</span></>}
        right={<button onClick={() => exportCsv(rows, pagination.currentPage)} disabled={!rows.length} className={btnCls + " disabled:opacity-40"}><Download size={14} />Export page</button>}
      >
        <FilterBar>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#878AA2]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, user id, round, tx id…" className={inputCls + " pl-9 w-60"} />
          </div>
          <input value={userId} onChange={(e) => setUserId(e.target.value.replace(/\D/g, ""))} placeholder="User ID" className={inputCls + " w-32"} />
          <input value={game} onChange={(e) => setGame(e.target.value)} placeholder="Game code" className={inputCls + " w-32"} />
          <select value={kind} onChange={(e) => setKind(e.target.value)} className={inputCls}>
            <option value="">All types</option>
            {Object.keys(KIND_COLORS).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
            <option value="">All statuses</option>
            <option value="applied">applied</option>
            <option value="noop">noop</option>
            <option value="refused">refused</option>
          </select>
          <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} placeholder="Currency" className={inputCls + " w-24"} />
          <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
          <TestToggle value={includeTest} onChange={setIncludeTest} />
        </FilterBar>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className={theadRow}>
              <th className={thCls}>Time</th><th className={thCls}>Player</th><th className={thCls}>Game</th><th className={thCls}>Type</th>
              <th className={thCls}>Status</th><th className={thR}>Amount</th><th className={thR}>Balance after</th><th className={thCls}>Round</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={tbodyRow}>
                  <td className={tdCls + " text-[#878AA2]"}>{fmtDate(r.created_at)}</td>
                  <PlayerCell name={r.user_name} id={r.user_id} />
                  <td className={tdCls + " capitalize"}>{r.game_code || "—"}</td>
                  <td className={tdCls}><Badge text={r.kind} map={KIND_COLORS} /></td>
                  <td className={tdCls}>
                    <span className={STATUS_COLORS[r.status] || ""}>{r.status}</span>
                    {r.error_code && <div className="text-xs text-[#E01B4F]">{r.error_code}</div>}
                  </td>
                  <td className={`${tdR} ${amountColor(r.kind)}`}>{fmtAmt(r.amount_major)} {r.currency}</td>
                  <td className={tdR + " text-[#878AA2]"}>{fmtAmt(r.balance_after)}</td>
                  <td className={tdCls}>
                    {r.round_id
                      ? <button onClick={() => setRound(r.round_id)} className="font-mono text-xs text-[#4A85F7] hover:underline" title={r.round_id}>{short(r.round_id, 14)}</button>
                      : "—"}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && <EmptyRow cols={8} text="No transactions match these filters" />}
            </tbody>
          </table>
        </div>
        <Pager p={pagination} onPage={setPage} />
      </Panel>
      {round && <RoundModal roundId={round} onClose={() => setRound(null)} />}
    </div>
  );
}
